import { Box, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SxProps, Theme } from '@mui/system';

import { getGradientBorderStyles } from '../styles/gradientBorderStyles';

const LEFT_ICON_SIZE = '32px';
const RADIO_BUTTON_CIRCLE_SIZE = '20px';
const INNER_RADIO_BUTTON_CIRCLE_SIZE = '12px';

export interface IMplRadioButtonStyledProps {
	disabled?: boolean;
}

export interface IStyledRadioButtonContainerProps
	extends IMplRadioButtonStyledProps {
	checked?: boolean;
	hasIcon?: boolean;
	multiline?: boolean;
}

export const StyledRadioButtonContainer = styled(Stack, {
	shouldForwardProp: (prop) =>
		prop !== 'disabled' &&
		prop !== 'checked' &&
		prop !== 'hasIcon' &&
		prop !== 'multiline',
})<IStyledRadioButtonContainerProps>(
	({ theme, disabled, checked, hasIcon, multiline }) => ({
		width: '100%',
		...(multiline ? { minHeight: '58px' } : { height: '58px' }),
		boxSizing: 'border-box',
		position: 'relative',
		border:
			checked && !disabled
				? 'none'
				: `0.5px solid ${
						disabled
							? theme.palette.solutions?.[100]
							: theme.palette.solutions?.[600]
				  }`,
		borderRadius: '8px',
		flexDirection: 'row',
		alignItems: 'center',
		paddingLeft: hasIcon ? '8px' : '12px',
		paddingRight: '12px',
		cursor: disabled ? 'not-allowed' : 'pointer',
		backgroundColor: 'transparent',
		opacity: disabled ? 0.4 : 1,
		boxShadow: checked ? '0px 4px 6px -4px rgba(24, 39, 75, 0.12)' : 'none',
		transition:
			'border-color 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease',
		'&:hover': {
			borderColor: disabled
				? theme.palette.solutions?.[100]
				: theme.palette.solutions?.[600],
		},
		...(checked && !disabled && getGradientBorderStyles(theme)),
	})
);

export const StyledIconWrapper = styled(Stack, {
	shouldForwardProp: (prop) => prop !== 'disabled',
})<IMplRadioButtonStyledProps>(() => ({
	marginRight: '8px',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: LEFT_ICON_SIZE,
	height: LEFT_ICON_SIZE,
	'& > *': {
		width: '100%',
		height: '100%',
	},
}));

export const TRUNCATE_TEXT_SX_PROPS: SxProps = {
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

export const getRadioSxProps = (theme: Theme): SxProps => ({
	padding: 0,
	flexShrink: 0,
	'&.Mui-checked': {
		color: theme.palette.primary.main,
	},
});

export const StyledRadioButtonCircle = styled(Stack, {
	shouldForwardProp: (prop) => prop !== 'disabled',
})<IMplRadioButtonStyledProps>(({ theme }) => ({
	alignItems: 'center',
	justifyContent: 'center',
	width: RADIO_BUTTON_CIRCLE_SIZE,
	height: RADIO_BUTTON_CIRCLE_SIZE,
	borderRadius: '50%',
	border: `2px solid ${theme.palette.primary.main}`,
	backgroundColor: 'transparent',
}));

export const StyledRadioButtonInnerCircle = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'disabled',
})<IMplRadioButtonStyledProps>(({ theme }) => ({
	width: INNER_RADIO_BUTTON_CIRCLE_SIZE,
	height: INNER_RADIO_BUTTON_CIRCLE_SIZE,
	borderRadius: '50%',
	backgroundColor: theme.palette.primary.main,
}));
